import { Component, ElementRef, HostListener, Input, ViewChild, signal, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-resizable-container',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="flex h-full w-full" [class.select-none]="isDragging()" [class.flex-row]="isRow()" [class.flex-col]="!isRow()">
      <!-- Left/Top Section -->
      <div [style.flex]="'0 0 ' + effectivePercent() + '%'" class="overflow-hidden min-w-0 min-h-0 relative">
        <ng-content select="[left]"></ng-content>
        <ng-content select="[top]"></ng-content>
        @if (isDragging()) {
          <div class="absolute inset-0 z-50"></div>
        }
      </div>

      <!-- Separator (hidden when stacked: panes are fixed 50/50 below the breakpoint) -->
      @if (!isStacked()) {
        <div
          #separator
          (mousedown)="onMouseDown($event)"
          (touchstart)="onTouchStart($event)"
          class="flex items-center justify-center group z-10 transition-colors"
          [class.cursor-col-resize]="orientation === 'horizontal'"
          [class.cursor-row-resize]="orientation === 'vertical'"
          [class.mx-0.5]="orientation === 'horizontal'"
          [class.my-0.5]="orientation === 'vertical'"
          [style.width.px]="orientation === 'horizontal' ? 6 : undefined"
          [style.height.px]="orientation === 'vertical' ? 6 : undefined"
        >
          <div
            class="rounded-full transition-colors"
            [class.h-10]="orientation === 'horizontal'"
            [class.w-1]="orientation === 'horizontal'"
            [class.w-10]="orientation === 'vertical'"
            [class.h-1]="orientation === 'vertical'"
            [class.bg-zinc-300]="!isDragging()"
            [class.dark:bg-zinc-600]="!isDragging()"
            [class.bg-blue-500]="isDragging()"
            [class.group-hover:bg-blue-400]="!isDragging()"
          ></div>
        </div>
      }

      <!-- Right/Bottom Section -->
      <div class="flex-1 overflow-hidden min-w-0 min-h-0 relative">
        <ng-content select="[right]"></ng-content>
        <ng-content select="[bottom]"></ng-content>
        @if (isDragging()) {
          <div class="absolute inset-0 z-50"></div>
        }
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    :host {
      display: block;
      height: 100%;
      width: 100%;
    }
  `]
})
export class ResizableContainerComponent implements OnInit {
  @Input() orientation: 'horizontal' | 'vertical' = 'horizontal';
  @Input() initialPercent = 50;
  /** Below this viewport width the panes stack vertically 50/50 and resizing is disabled. 0 = never stack. */
  @Input() stackBelowPx = 0;
  /** When set, the split percent is persisted to localStorage under this key. */
  @Input() storageKey: string | null = null;

  splitPercent = signal(50);
  isDragging = signal(false);
  isStacked = signal(false);

  @ViewChild('separator') separatorElement!: ElementRef;

  public ngOnInit() {
    this.splitPercent.set(this.restorePercent() ?? this.initialPercent);
    this.updateStacked();
  }

  /** Row layout for horizontal orientation unless stacked by the breakpoint. */
  isRow(): boolean {
    return this.orientation === 'horizontal' && !this.isStacked();
  }

  effectivePercent(): number {
    return this.isStacked() ? 50 : this.splitPercent();
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.updateStacked();
  }

  private updateStacked(): void {
    this.isStacked.set(this.stackBelowPx > 0 && window.innerWidth < this.stackBelowPx);
  }

  onMouseDown(event: MouseEvent) {
    event.preventDefault();
    this.isDragging.set(true);
  }

  onTouchStart(event: TouchEvent) {
    this.isDragging.set(true);
  }

  @HostListener('window:mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    if (!this.isDragging()) return;
    this.updateSplit(event.clientX, event.clientY);
  }

  @HostListener('window:touchmove', ['$event'])
  onTouchMove(event: TouchEvent) {
    if (!this.isDragging()) return;
    const touch = event.touches[0];
    this.updateSplit(touch.clientX, touch.clientY);
  }

  @HostListener('window:mouseup')
  onMouseUp() {
    this.endDrag();
  }

  @HostListener('window:touchend')
  onTouchEnd() {
    this.endDrag();
  }

  private endDrag(): void {
    if (!this.isDragging()) return;
    this.isDragging.set(false);
    this.persistPercent();
  }

  private restorePercent(): number | null {
    if (!this.storageKey) return null;
    try {
      const stored = Number(localStorage.getItem(this.storageKey));
      return stored >= 10 && stored <= 90 ? stored : null;
    } catch {
      return null;
    }
  }

  private persistPercent(): void {
    if (!this.storageKey) return;
    try {
      localStorage.setItem(this.storageKey, String(Math.round(this.splitPercent())));
    } catch {
      // localStorage unavailable (private mode etc.) — resize still works, just not persisted
    }
  }

  private updateSplit(clientX: number, clientY: number) {
    const container = this.separatorElement.nativeElement.parentElement;
    const rect = container.getBoundingClientRect();

    let percent: number;
    if (this.orientation === 'horizontal') {
      percent = ((clientX - rect.left) / rect.width) * 100;
    } else {
      percent = ((clientY - rect.top) / rect.height) * 100;
    }

    // Constraints
    if (percent < 10) percent = 10;
    if (percent > 90) percent = 90;

    this.splitPercent.set(percent);
  }
}
